import React from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Share2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import AnimatedSection from "../components/common/AnimatedSection";
import Button from "../components/common/Button";
import { useBookingModal } from "../contexts/BookingModalContext";

// Placeholder blog content (in production, this would come from markdown files)
const blogContent = {
  "choosing-right-materials-kitchen": {
    title: "Choosing the Right Materials for Your Kitchen",
    date: "2024-12-01",
    readTime: "5 min",
    category: "Materials",
    image:
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=80",
    content: `
When it comes to kitchen interiors, material choice can make or break your investment. At Iravatham, we're often asked: "Why don't you use MDF like other companies?" The answer lies in understanding what each material offers.

## Why We Don't Use MDF

MDF (Medium Density Fibreboard) is popular because it's cheap. But in Indian kitchens – where heat, moisture, and heavy use are the norm – MDF fails within 3-5 years. It absorbs moisture, swells, and degrades.

## Our Material Choices

### MR Ply (Moisture Resistant)
Ideal for dry areas like upper cabinets and shelving. MR Ply handles normal humidity well and offers excellent strength-to-weight ratio.

### BWR Ply (Boiling Water Resistant)
Perfect for semi-wet areas. BWR Ply can handle occasional water exposure without damage – think the area around your sink splashback.

### BWP Ply (Boiling Water Proof)
The premium choice for wet areas. BWP Ply is marine-grade and completely waterproof. We use it for under-sink cabinets and areas with high moisture exposure.

### HDHMR (High Density High Moisture Resistant)
A modern alternative that combines density with moisture resistance. Excellent for drawer boxes and internal components.

## How We Select Materials

At our factory, material selection is systematic:

1. **Zone mapping** – We identify wet, semi-wet, and dry zones in your kitchen
2. **Usage analysis** – Heavy-use areas get BWP Ply; lighter areas get MR Ply
3. **Budget optimization** – We use premium materials where they matter most

## The Result

Kitchens that last 15-20 years, not 3-5. That's the Iravatham difference.

Ready to discuss materials for your kitchen? [Book a consultation](/contact) with our design experts.
    `,
  },
  "modular-vs-carpentry-comparison": {
    title: "Modular vs Carpentry: A Detailed Comparison",
    date: "2024-11-15",
    readTime: "7 min",
    category: "Guide",
    image:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80",
    content: `
The age-old debate: should you go with a local carpenter or choose modular interiors? Let's break it down based on what actually matters.

## Precision & Finish

**Carpentry:** Manual measurements and cuts mean human error is inevitable. Even skilled carpenters work with 2-3mm tolerances.

**Modular (Factory):** CNC machines like our Biesse equipment work with sub-millimeter precision. Every joint, every edge is perfect.

## Timeline

**Carpentry:** Typically 45-60 days, often delayed due to labor issues, material shortages, or weather.

**Modular (Factory):** 20 days for most projects. Factory production isn't affected by weather or on-site issues.

## Material Quality

**Carpentry:** Varies widely. Many carpenters use MDF to cut costs. Quality depends on supervision.

**Modular (Iravatham):** Consistent quality with MR, BWR, BWP Ply and HDHMR. No MDF, ever.

## Cost

**Carpentry:** Lower upfront cost, but higher long-term cost due to repairs and replacements.

**Modular (Factory):** Higher upfront investment, but 10-year warranty and decades of durability.

## The Verdict

If you're building for 3-5 years, carpentry might work. If you're building for 15-20 years, factory-fit modular is the clear choice.

[See our factory](/factory) and the precision that goes into every piece.
    `,
  },
};

// Default content for posts not yet written
const defaultPost = {
  title: "Article Coming Soon",
  date: new Date().toISOString().split("T")[0],
  readTime: "5 min",
  category: "General",
  image:
    "https://images.unsplash.com/photo-1615529182904-14819c35db37?w=1200&q=80",
  content: `
This article is being crafted by our team and will be available soon.

In the meantime, explore our other articles or [contact us](/contact) to discuss your interior project.
  `,
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const { openModal } = useBookingModal();
  const post = blogContent[slug] || {
    ...defaultPost,
    title: slug
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "),
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative py-32 lg:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-primary via-brand-primary/70 to-brand-primary/40" />
        </div>
        <div className="container-custom section-padding relative">
          <AnimatedSection className="max-w-3xl text-white">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            <span className="inline-block px-3 py-1 bg-brand-accent rounded-full text-sm font-medium mb-4 ml-5">
              {post.category}
            </span>
            <h1 className="font-heading text-h1 mb-6 text-white">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-white/80">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {new Date(post.date).toLocaleDateString("en-IN", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {post.readTime} read
              </span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Content */}
      <section className="section-spacing bg-white">
        <div className="container-custom section-padding">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <article
                className="prose prose-lg prose-slate max-w-none 
                prose-headings:font-heading prose-headings:text-brand-primary 
                prose-headings:leading-tight
                prose-h1:text-4xl prose-h1:mt-20 prose-h1:mb-8 prose-h1:leading-tight prose-h1:first:mt-0
                prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-7 prose-h2:leading-tight
                prose-h3:text-2xl prose-h3:mt-12 prose-h3:mb-6 prose-h3:leading-tight
                prose-p:text-lg prose-p:leading-[1.8] prose-p:mb-6 prose-p:mt-0
                prose-a:text-brand-accent prose-a:no-underline hover:prose-a:underline
                prose-ul:my-8 prose-ul:pl-8 prose-ul:list-disc prose-ul:space-y-2
                prose-ol:my-8 prose-ol:pl-8 prose-ol:list-decimal prose-ol:space-y-2
                prose-li:text-lg prose-li:leading-[1.8] prose-li:my-2 prose-li:pl-1
                prose-strong:text-brand-primary prose-strong:font-semibold
                prose-blockquote:border-l-4 prose-blockquote:border-brand-accent prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-text-secondary prose-blockquote:my-8"
              >
                <ReactMarkdown>{post.content}</ReactMarkdown>
              </article>
            </AnimatedSection>

            {/* Share & CTA */}
            <AnimatedSection className="mt-16 pt-12 border-t border-border-light">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div>
                  <h4 className="font-heading text-lg text-brand-primary mb-2">
                    Found this helpful?
                  </h4>
                  <p className="text-text-secondary text-sm">
                    Share it with someone planning their interiors.
                  </p>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 border border-border-light rounded-full text-text-secondary hover:text-brand-primary hover:border-brand-primary transition-colors">
                  <Share2 className="w-4 h-4" />
                  Share Article
                </button>
              </div>
            </AnimatedSection>

            {/* CTA */}
            <AnimatedSection className="mt-16 bg-gradient-brand rounded-3xl p-8 lg:p-12 text-center text-white">
              <h3 className="font-heading text-2xl mb-4 text-white">
                Ready to start your interior project?
              </h3>
              <p className="text-white/80 mb-6">
                Book a consultation with our design experts.
              </p>
              <Button onClick={openModal} variant="white">
                Book Consultation
              </Button>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
